import CustomTooltip from '../../../shared/CustomTooltip';
import {
    Box,
    Card,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    APIProvider,
    ControlPosition,
    Map,
    MapControl,
    MapEvent,
    useMap,
} from '@vis.gl/react-google-maps';
import React, { useEffect, useMemo, useState } from 'react';
import { FormValues as Experience } from '../../../types/experience';
import MapMarker from './MapMarker';
import { NavigateNext } from '@mui/icons-material';
import {
    selectExperienceFilters,
    setExperienceFilters,
} from '../../../../app/Slices/ExperienceFiltersSlice';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import LiveLocationCircle from './LiveLocationCircle';
import { useGetAllVehiclesQuery } from '../../../../app/Slices/SevenDayApiSlice';

interface ExperienceMapProps {
    experiences: Array<Experience>;
    handleClose: () => void;
}

const css = `
    .gm-style .gm-style-iw {
        padding: 0 !important;
    } 
    .gm-style .gm-style-iw-d {
        overflow: auto !important
    }

    .marker-animate {
        animation: markerPopIn 0.3s ease-in-out;
        transform-origin: center;
    }

    @keyframes markerPopIn {
        0% {
            opacity: 0;
            transform: scale(0.5);
        }
        100% {
            opacity: 1;
            transform: scale(1);
        }
    }
`;

const MapHandler = ({ bounds }: any) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        if (bounds) {
            map.fitBounds(bounds);
        }
    }, [map, bounds]);

    return null;
};

const ExperienceMap = ({ experiences, handleClose }: ExperienceMapProps) => {
    const MAP_ID = '9064210e52cc2181f0dc0a4e';
    const dispatch = useDispatch();

    const isMobile = useMediaQuery('(max-width: 770px)');
    const theme = useTheme();

    const filters = useSelector(selectExperienceFilters);

    const [center, setCenter] = useState<google.maps.LatLngLiteral>({
        lat: -33.865143,
        lng: 151.2099,
    });

    const { data: vehicles } = useGetAllVehiclesQuery();

    const handleIdle = useMemo(
        () =>
            debounce((e: MapEvent) => {
                const bounds = e.map.getBounds();
                if (!bounds) return;

                dispatch(
                    setExperienceFilters({
                        ...filters,
                        swLat: bounds.getSouthWest().lat(),
                        swLng: bounds.getSouthWest().lng(),
                        neLat: bounds.getNorthEast().lat(),
                        neLng: bounds.getNorthEast().lng(),
                    }),
                );
            }, 500),
        [dispatch, filters],
    );

    useEffect(() => {
        return () => {
            handleIdle.cancel();
        };
    }, [handleIdle]);

    return (
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
            <style>{css}</style>
            <Card
                sx={{
                    height: '100%',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Map
                        disableDefaultUI
                        defaultZoom={10}
                        onCenterChanged={(e) => {
                            setCenter(e.detail.center);
                        }}
                        onIdle={handleIdle}
                        center={center}
                        mapId={MAP_ID}
                        fullscreenControl
                        gestureHandling='greedy'
                    >
                        {!isMobile && (
                            <MapControl position={ControlPosition.TOP_LEFT}>
                                <IconButton
                                    aria-label='Hide map'
                                    sx={{
                                        backgroundColor:
                                            theme.palette.neutral.main,
                                        boxShadow: 1,
                                        m: 1,
                                    }}
                                    onClick={handleClose}
                                >
                                    <CustomTooltip title={'Hide map'}>
                                        <NavigateNext />
                                    </CustomTooltip>
                                </IconButton>
                            </MapControl>
                        )}

                        {experiences.map((experience) => (
                            <MapMarker
                                key={experience.id}
                                experience={experience}
                            />
                        ))}
                    </Map>
                    <MapHandler bounds={filters.bounds} />
                </Box>
                {vehicles && vehicles.length > 0 && (
                    <Box
                        sx={{
                            flexShrink: 0,
                            px: 1,
                            py: 0.75,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontStyle: 'italic',
                            }}
                        >
                            <LiveLocationCircle /> indicates the live location
                            of the vehicles, updated every 30 minutes. The live
                            locations of the vehicles currently in use are not
                            shown.
                        </Typography>
                    </Box>
                )}
            </Card>
        </APIProvider>
    );
};

export default ExperienceMap;
